import type { Review } from 'components/supplier_show/prop_shapes/review'

export const REVIEWER_DISPLAY_STATES = [
  'ALL_VISIBLE',
  'RECIPIENT_ANONYMOUS',
  'SENDER_ANONYMOUS'
] as const

export type ReviewerDisplayState = (typeof REVIEWER_DISPLAY_STATES)[number]

export const CONVERSATION_TYPES = [
  'general',
  'review',
  'issue'
] as const

export type ConversationType = (typeof CONVERSATION_TYPES)[number]

export interface InboxParticipant {
  firstName: string
  fullName: string
  display: 'full' | 'anonymous'
  orgName: string | null
  avatarUrl?: string
}

export interface InboxSender extends InboxParticipant {
  isCurrentUser: boolean
}

export interface InboxMessage {
  id: number
  conversationId: number
  sender: InboxSender
  body: string
  createdAt: string
  system?: 'canary'
}

export interface InboxIssue {
  createdAt: string
  userName: string
  supplierLinkMode: 'default' | 'canary'
}

// GET /conversations
export interface InboxConversationSummary {
  id: number
  conversationType: ConversationType
  participants: InboxParticipant[]
  lastMessageSenderName: string
  lastMessageSenderIsCurrentUser?: boolean
  lastMessageText: string
  unreadMessages: boolean
}

export interface ApiInboxParticipantDisplay {
  first_name: string
  full_name: string
  display: 'full' | 'anonymous'
  organization_name: string | null
  thumbnail: string | null
}

export interface ApiInboxSenderDisplay extends ApiInboxParticipantDisplay {
  is_current_user: boolean
}

export interface ApiInboxConversationSummary {
  id: number
  conversation_type: ConversationType
  additional_participants: ApiInboxParticipantDisplay[]
  last_message_trunc: string | null
  last_message_sender?: ApiInboxSenderDisplay | null
  unread_messages: boolean
}

export interface ApiInboxConversationsIndexResponse {
  conversations: ApiInboxConversationSummary[]
}

export const normalizeLastMessageSender = (
  sender: ApiInboxSenderDisplay | null | undefined
): ApiInboxSenderDisplay | null => {
  if (sender == null) return null
  if (Object.keys(sender).length === 0) return null
  return sender
}

export interface ApiInboxMessage {
  id: number
  sender: ApiInboxSenderDisplay
  body: string
  created_at: string
  system?: 'canary'
}

export interface ApiInboxIssue {
  created_at: string
  user_name: string
  supplier_link_mode?: 'default' | 'canary'
}

export interface ApiInboxConversationDetail {
  id: number
  conversation_type: ConversationType
  additional_participants: ApiInboxParticipantDisplay[]
  messages: ApiInboxMessage[]
  review: Review | null
  issue?: ApiInboxIssue | null
}

export interface ApiInboxConversationShowResponse {
  conversation: ApiInboxConversationDetail
}

const mapApiDisplayToInboxParticipantBase = (
  participant: ApiInboxParticipantDisplay
): InboxParticipant => ({
  firstName: participant.first_name,
  fullName: participant.full_name,
  display: participant.display,
  orgName: participant.organization_name,
  avatarUrl: participant.thumbnail ?? undefined
})

export const mapApiParticipantToInboxParticipant = (
  participant: ApiInboxParticipantDisplay
): InboxParticipant => mapApiDisplayToInboxParticipantBase(participant)

export const mapApiSenderToInboxSender = (
  sender: ApiInboxSenderDisplay
): InboxSender => ({
  ...mapApiDisplayToInboxParticipantBase(sender),
  isCurrentUser: sender.is_current_user
})

export const mapApiSummaryToInboxSummary = (
  summary: ApiInboxConversationSummary
): InboxConversationSummary => {
  const lastMessageSender = normalizeLastMessageSender(summary.last_message_sender)
  const hasLastMessage = summary.last_message_trunc != null

  return {
    id: summary.id,
    conversationType: summary.conversation_type,
    participants: summary.additional_participants.map(mapApiParticipantToInboxParticipant),
    lastMessageSenderName: hasLastMessage ? (lastMessageSender?.full_name ?? '') : '',
    lastMessageSenderIsCurrentUser: hasLastMessage ? (lastMessageSender?.is_current_user ?? false) : false,
    lastMessageText: summary.last_message_trunc ?? '',
    unreadMessages: summary.unread_messages
  }
}

// GET /conversations/:id
export interface InboxConversationDetail {
  id: number
  conversationType: ConversationType
  participants: InboxParticipant[]
  messages: InboxMessage[]
  review: Review | null
  issue: InboxIssue | null
}

export const mapApiMessageToInboxMessage = (
  message: ApiInboxMessage,
  conversationId: number
): InboxMessage => ({
  id: message.id,
  conversationId,
  sender: mapApiSenderToInboxSender(message.sender),
  body: message.body,
  createdAt: message.created_at,
  system: message.system
})

export const mapApiIssueToInboxIssue = (
  issue: ApiInboxIssue
): InboxIssue => ({
  createdAt: issue.created_at,
  userName: issue.user_name,
  supplierLinkMode: issue.supplier_link_mode ?? 'default'
})

export const mapApiDetailToInboxDetail = (
  detail: ApiInboxConversationDetail
): InboxConversationDetail => ({
  id: detail.id,
  conversationType: detail.conversation_type,
  participants: detail.additional_participants.map(mapApiParticipantToInboxParticipant),
  messages: detail.messages.map(message => mapApiMessageToInboxMessage(message, detail.id)),
  review: detail.review,
  issue: detail.issue == null ? null : mapApiIssueToInboxIssue(detail.issue)
})
